import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Circle,
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  VStack
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { InsuredProfilePageDuskIds } from '../../constants/duskIds';
import radii from '../../styles/theme/foundation/radii';
import { createMap } from '../../utils/arrayUtil';
import LoadingSpinner from '../Spinner';

const ModalSideBarMenu = ({ isOpen, onClose, sideBarTitle, options, refreshFunction = () => {} }) => {
  const [selectedOption, setSelectedOption] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const menuMap = createMap(options.menuOptions, 'menuId');

  const getSideBarTitleInitials = title => title.split(' ').slice(0, 3).map(word => word[0]).join('');

  const refresh = () => {
    setIsRefreshing(true);
    refreshFunction(() => setIsRefreshing(false));
  };

  const handleMenuChange = menuId => () => setSelectedOption(menuId);

  return (
    <Modal borderRadius="md" isOpen={isOpen} size="5xl" onClose={onClose}>
      <ModalOverlay />
      <ModalContent height="650px" p={0}>
        <ModalCloseButton zIndex={2} />
        <ModalBody p={0}>
          <Flex height="100%" maxW="100%">
            <Box
              bg="white"
              borderRadius={`${radii.md} 0px 0px ${radii.md}`}
              boxShadow="base2"
              flexBasis="25%"
              height="650px"
              width="30%"
              zIndex={2}
            >
              <VStack justifyContent="center" pb="30px" pt="43px" spacing="22px" textStyle="bodyXl">
                <Circle
                  bgColor={isRefreshing ? 'transparent' : options.sideBarIconBackgroundColor}
                  color={options.sideBarIconColor}
                  display={options.hideSideBarIcon ? 'none' : 'inherit'}
                  dusk={InsuredProfilePageDuskIds.insuredRefreshButton}
                  size="70px"
                  variant="ghost"
                  onDoubleClick={() => refresh()}
                >
                  {
                    isRefreshing
                      ? <LoadingSpinner isInline />
                      : getSideBarTitleInitials(sideBarTitle)
                  }
                </Circle>
                <Text
                  overflowWrap
                  color="greyScale.900"
                  maxWidth="80%"
                  px={2}
                  textAlign="center"
                  textStyle="displayXxs"
                >
                  {sideBarTitle}
                </Text>
              </VStack>

              <VStack spacing={0}>
                { options.menuOptions.map(menuOption => (
                  <Flex
                    _hover={{ cursor: 'pointer' }}
                    alignItems="center"
                    bg={menuOption.menuId === selectedOption ? '#E1F5FF' : 'white'}
                    borderLeft="4px"
                    borderLeftColor={menuOption.menuId === selectedOption ? 'primaryScale.600' : 'white'}
                    data-analytics-id={menuOption.dataAnalyticsId}
                    height="60px"
                    id={menuOption.menuId}
                    justifyContent="flex-start"
                    key={menuOption.menuId}
                    pl="16px"
                    width="100%"
                    onClick={handleMenuChange(menuOption.menuId)}
                  >
                    <Icon
                      as={menuOption.icon}
                      boxSize="15px"
                      color={menuOption.menuId === selectedOption ? 'primaryScale.600' : 'greyScale.700'}
                      mr="10px"
                    />
                    <Text
                      color={menuOption.menuId === selectedOption ? 'primaryScale.600' : 'greyScale.700'}
                      textStyle={menuOption.menuId === selectedOption ? 'bodyLg600' : 'bodyLg500'}
                    >
                      {menuOption.menuText}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            </Box>
            <Flex
              alignItems="stretch"
              bg="white"
              borderRadius={`0px ${radii.md} ${radii.md} 0px`}
              flexBasis="75%"
              height="650px"
              pb="20px"
            >
              {menuMap[selectedOption].component(onClose)}
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

ModalSideBarMenu.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sideBarTitle: PropTypes.string.isRequired,
  refreshFunction: PropTypes.func,
  options: PropTypes.shape({
    hideSideBarIcon: PropTypes.bool,
    sideBarIconBackgroundColor: PropTypes.string,
    sideBarIconColor: PropTypes.string,
    currentMenuId: PropTypes.number,
    menuOptions: PropTypes.arrayOf(PropTypes.shape({
      menuId: PropTypes.number,
      menuText: PropTypes.string,
      selected: PropTypes.bool,
      component: PropTypes.func,
      icon: PropTypes.func,
      dataAnalyticsId: PropTypes.string
    })).isRequired
  })
};

ModalSideBarMenu.defaultProps = {
  options: {
    hideSideBarIcon: false,
    sideBarIconColor: '#00BCAD',
    menuOptions: []
  }
};

export default ModalSideBarMenu;
